/* eslint-disable react/no-array-index-key */
import { useEffect, useMemo, useState } from 'react'
import './CreateEditProfile.scss'
import PlusIcon from '@rsuite/icons/Plus'
import {
  SelectPicker,
  Panel,
  ButtonToolbar,
  Col,
  Input,
  Whisper,
  Tooltip
} from 'rsuite'

import {
  Button,
  FormikErrorMessage,
  Modal,
  Row,
  Section,
  TextInput,
  Uploader
} from 'shared'
import { notifyError, notifySuccess, previewFile } from 'utils'
import { Formik, Form, FormikHelpers, FormikProps, FieldArray } from 'formik'
import { MdRemoveCircle } from 'react-icons/md'
import {
  useGetCountryQuery,
  useGetDistrictsQuery,
  useGetProvincesQuery,
  useGetSubDistrictsQuery,
  useGetVillagesQuery
} from 'pages/audience/audienceApiSlice'
import { skipToken } from '@reduxjs/toolkit/query'
import useAuth from '../../../hooks/Auth'
import {
  PROFILE_FORM_FIELDS,
  IProfileForm,
  defaultProfileFormValues,
  getInitialProfileFormValueFromResponse,
  profileValidationSchema,
  convertFileToBase64,
  DOCUMENT_TYPE
} from '../utils'
import {
  useCreateProfileMutation,
  useUpdateProfileMutation
} from '../profileApiSlice'

import { Profile } from '../types'

interface CreateProfileProps {
  open: boolean;
  onClose: () => void;
  profile?: any;
  isEditMode: boolean;
  setIsEditMode?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreateEditProfile: React.FC<CreateProfileProps> = ({
  open,
  onClose,
  isEditMode,
  profile
}) => {
  const [createProfile] = useCreateProfileMutation()
  const { setIsSubsId } = useAuth()
  const maxSize = 2 * 1024 * 1024
  const [fileInfo, setFileInfo] = useState<string | undefined>(undefined)
  const [editProfile] = useUpdateProfileMutation()
  const {
    NAME,
    EMAIL,
    WEBSITE_URL,
    PHONE_NO,
    DOCUMENTDTO,
    USER_EMAIL,
    USER_FIRSTNAME,
    USER_LASTNAME,
    imageUrl,
    DISTRICT,
    SUB_DISTRICT,
    Village,
    ADDRESS,
    COUNTRY,
    PROVINCES
  } = PROFILE_FORM_FIELDS

  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(
    null
  )
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null)
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(
    null
  )
  const [selectedSubDistrictId, setSelectedSubDistrictId] = useState<
    number | null
  >(null)
  const companyId = profile?.company?.id
  const MAX_FILE_SIZE = 2 * 1024 * 1024
  const initialValues = useMemo(
    () =>
      companyId && profile
        ? getInitialProfileFormValueFromResponse(profile)
        : defaultProfileFormValues,
    [companyId, profile]
  )
  useEffect(() => {
    if (companyId) {
      if (profile?.user?.imageUrl) {
        setFileInfo(profile?.user?.imageUrl)
      }
    }
  }, [companyId, profile])

  const { data: countryData, isLoading: isCountryLoading } =
    useGetCountryQuery()
  const { data: statesData, isLoading: isStatesLoading } = useGetProvincesQuery(
    selectedCountryId ?? skipToken,
    {
      skip: selectedCountryId === null
    }
  )

  const { data: districtsData, isLoading: isDistrictsLoading } =
    useGetDistrictsQuery(selectedStateId ?? skipToken, {
      skip: selectedStateId === null
    })

  const { data: subDistrictsData, isLoading: isSubDistrictsLoading } =
    useGetSubDistrictsQuery(selectedDistrictId ?? skipToken, {
      skip: selectedDistrictId === null
    })

  const { data: villageData, isLoading: isVillageDataLoading } =
    useGetVillagesQuery(selectedSubDistrictId ?? skipToken, {
      skip: selectedSubDistrictId === null
    })

  const generateOptions: any = (data: any | undefined, isLoading: boolean) =>
    isLoading
      ? []
      : data?.map((item: any) => ({
          label: item.name,
          value: item.id
        })) || []

  const countryOptions = generateOptions(countryData, isCountryLoading)
  const stateOptions = generateOptions(statesData, isStatesLoading)
  const districtOptions = generateOptions(districtsData, isDistrictsLoading)
  const subDistrictOptions = generateOptions(
    subDistrictsData,
    isSubDistrictsLoading
  )
  const VillageOptions = generateOptions(villageData, isVillageDataLoading)

  useEffect(() => {
    if (profile && profile.company) {
      setSelectedCountryId(profile.company.country?.id ?? null)
      setSelectedStateId(profile.company.state?.id ?? null)
      setSelectedDistrictId(profile.company.district?.id ?? null)
      setSelectedSubDistrictId(profile.company.taluka?.id ?? null)
    }
  }, [profile])

  const onSubmit = async (
    formValues: IProfileForm,
    { setSubmitting }: FormikHelpers<IProfileForm>
  ) => {
    if (!companyId && formValues[DOCUMENTDTO].length !== 3) {
      notifyError('Please upload 3 documents', { containerId: 'modalToast' })
      setSubmitting(false)
      return
    }
    let processedDocuments: any[] = []
    if (companyId) {
      processedDocuments = await Promise.all(
        formValues[DOCUMENTDTO]?.map(async (doc) => {
          if (doc.imageUrls && typeof doc.imageUrls === 'string') {
            if (doc.imageUrls.startsWith('http')) {
              const response = await fetch(doc.imageUrls)
              const blob = await response.blob()
              const file = new File([blob], 'document.jpg', { type: blob.type })
              const base64 = await convertFileToBase64(file)
              return { ...doc, imageUrls: base64 }
            }

            return doc
          }

          if (doc.imageUrls instanceof File) {
            const base64 = await convertFileToBase64(doc.imageUrls)
            return { ...doc, imageUrls: base64 }
          }

          return doc
        }) || []
      )
    }

    const payload: Profile = {
      company: {
        [PROFILE_FORM_FIELDS.ID]: companyId,
        [PROFILE_FORM_FIELDS.NAME]: formValues[PROFILE_FORM_FIELDS.NAME],
        [PROFILE_FORM_FIELDS.ADDRESS]: formValues[PROFILE_FORM_FIELDS.ADDRESS],
        [PROFILE_FORM_FIELDS.EMAIL]: formValues[PROFILE_FORM_FIELDS.EMAIL],
        [PROFILE_FORM_FIELDS.WEBSITE_URL]:
          formValues[PROFILE_FORM_FIELDS.WEBSITE_URL],
        [PROFILE_FORM_FIELDS.COUNTRY]: {
          id: formValues[PROFILE_FORM_FIELDS.COUNTRY]
        },
        [PROFILE_FORM_FIELDS.PROVINCES]: {
          id: formValues[PROFILE_FORM_FIELDS.PROVINCES]
        },
        [PROFILE_FORM_FIELDS.DISTRICT]: {
          id: formValues[PROFILE_FORM_FIELDS.DISTRICT]
        },
        [PROFILE_FORM_FIELDS.SUB_DISTRICT]: {
          id: formValues[PROFILE_FORM_FIELDS.SUB_DISTRICT]
        },
        [PROFILE_FORM_FIELDS.Village]: {
          id: formValues[PROFILE_FORM_FIELDS.Village]
        },
        [PROFILE_FORM_FIELDS.PHONE_NO]: formValues[PROFILE_FORM_FIELDS.PHONE_NO]
      },
      user: {
        id: profile?.user?.id,
        [PROFILE_FORM_FIELDS.imageUrl]: formValues[PROFILE_FORM_FIELDS.imageUrl]
      },
      documentDTOS: companyId
        ? processedDocuments.map((doc) => ({
            documentType: doc.documentType,
            documentNo: doc.documentNo || '',
            imageUrls: doc.imageUrls
          }))
        : formValues.documentDTO?.map((doc) => ({
            documentType: doc.documentType,
            documentNo: doc.documentNo || '',
            imageUrls: doc.imageUrls
          }))
    }
    if (companyId) {
      delete (payload as any).documentDTOS
    }
    try {
      if (!companyId) {
        await createProfile(payload).unwrap()
        notifySuccess('Profile Created successfully!')
        onClose()
        setIsSubsId(true)
      } else {
        await editProfile({
          ...payload,
          id: companyId
        }).unwrap()
        notifySuccess('Profile updated successfully!')
        onClose()
      }
    } catch (error) {
      notifyError('Failed to save Profile', { containerId: 'modalToast' })
    } finally {
      setSubmitting(false)
    }
  }

  const renderFormButtons = (formikProps: FormikProps<IProfileForm>) => (
    <ButtonToolbar>
      {isEditMode ? (
        <>
          <Button
            className="formButton"
            id="reset"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="formButton"
            appearance="primary"
            type="submit"
            disabled={formikProps.isValidating ?? formikProps.isSubmitting}
          >
            Save
          </Button>
        </>
      ) : (
        <>
          <Button
            className="formButton"
            id="reset"
            onClick={() => {
              formikProps.resetForm()
            }}
          >
            Reset
          </Button>
          <Button
            className="formButton"
            type="submit"
            appearance="primary"
            disabled={formikProps.isValidating ?? formikProps.isSubmitting}
          >
            Save changes
          </Button>
        </>
      )}
    </ButtonToolbar>
  )

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      secondary
      open={open}
      onClose={handleClose}
      title={companyId ? 'Edit Profile' : 'Create Profile'}
      size="calc(70% - 120px)"
      body={
        <Formik
          initialValues={initialValues}
          validationSchema={profileValidationSchema}
          enableReinitialize
          onSubmit={onSubmit}
        >
          {(formikProps: FormikProps<IProfileForm>) => (
            <Form className="edit-profile">
              <Panel>
                <Section title="Company Information">
                  <Row>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={NAME}
                        placeholder="Enter company name"
                        dataType="string"
                      />
                      <FormikErrorMessage name={NAME} />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={EMAIL}
                        placeholder="Enter email"
                        dataType="email"
                      />
                      <FormikErrorMessage name={EMAIL} />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={PHONE_NO}
                        placeholder="Enter phone number"
                        dataType="string"
                      />
                      <FormikErrorMessage name={PHONE_NO} />
                    </Col>
                    <Col xs={12}>
                      <TextInput
                        formik={formikProps}
                        name={WEBSITE_URL}
                        placeholder="Enter website URL"
                        dataType="string"
                      />
                      <FormikErrorMessage name={WEBSITE_URL} />
                    </Col>
                  </Row>
                </Section>

                <Section title="Address Information">
                  <Row>
                    <Col xs={8}>
                      <SelectPicker
                        data={isCountryLoading ? [] : countryOptions}
                        placeholder={
                          isCountryLoading
                            ? 'Loading country...'
                            : 'Select Country'
                        }
                        value={formikProps.values[COUNTRY]}
                        onChange={(value) => {
                          const parsedValue =
                            value !== null ? Number(value) : null
                          formikProps.setFieldValue(COUNTRY, parsedValue)
                          setSelectedCountryId(parsedValue)
                        }}
                        block
                      />
                      <FormikErrorMessage name={COUNTRY} />
                    </Col>
                    <Col xs={8}>
                      <SelectPicker
                        data={isStatesLoading ? [] : stateOptions}
                        placeholder={
                          isStatesLoading ? 'Loading states...' : 'Select State'
                        }
                        value={formikProps.values[PROVINCES]}
                        onChange={(value) => {
                          const parsedValue =
                            value !== null ? Number(value) : null
                          formikProps.setFieldValue(PROVINCES, parsedValue)
                          setSelectedStateId(parsedValue)
                        }}
                        block
                        disabled={
                          !formikProps.values[COUNTRY] || isStatesLoading
                        }
                      />
                      <FormikErrorMessage name={PROVINCES} />
                    </Col>
                    <Col xs={8}>
                      <SelectPicker
                        data={isDistrictsLoading ? [] : districtOptions}
                        placeholder={
                          isDistrictsLoading
                            ? 'Loading districts...'
                            : 'Select District'
                        }
                        value={formikProps.values[DISTRICT]}
                        onChange={(value) => {
                          const parsedValue =
                            value !== null ? Number(value) : null
                          formikProps.setFieldValue(DISTRICT, parsedValue)
                          setSelectedDistrictId(parsedValue)
                        }}
                        block
                        disabled={
                          !formikProps.values[PROVINCES] || isDistrictsLoading
                        }
                      />
                      <FormikErrorMessage name={DISTRICT} />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={8}>
                      <SelectPicker
                        data={isSubDistrictsLoading ? [] : subDistrictOptions}
                        placeholder={
                          isSubDistrictsLoading
                            ? 'Loading Sub Districts...'
                            : 'Select SubDistrict'
                        }
                        value={formikProps.values[SUB_DISTRICT]}
                        onChange={(value) => {
                          const parsedValue =
                            value !== null ? Number(value) : null
                          formikProps.setFieldValue(SUB_DISTRICT, parsedValue)
                          setSelectedSubDistrictId(parsedValue)
                        }}
                        block
                        disabled={
                          !formikProps.values[DISTRICT] || isSubDistrictsLoading
                        }
                      />
                      <FormikErrorMessage name={SUB_DISTRICT} />
                    </Col>
                    <Col xs={8}>
                      <SelectPicker
                        data={isVillageDataLoading ? [] : VillageOptions}
                        placeholder={
                          isVillageDataLoading
                            ? 'Loading villages...'
                            : 'Select Village'
                        }
                        value={formikProps.values[Village]}
                        onChange={(value) => {
                          const parsedValue =
                            value !== null ? Number(value) : null
                          formikProps.setFieldValue(Village, parsedValue)
                        }}
                        block
                        disabled={
                          !formikProps.values[SUB_DISTRICT] ||
                          isVillageDataLoading
                        }
                      />
                      <FormikErrorMessage name={Village} />
                    </Col>
                    <Col xs={8}>
                      <TextInput
                        formik={formikProps}
                        name={ADDRESS}
                        placeholder="Enter Address"
                        dataType="string"
                      />
                      <FormikErrorMessage name={ADDRESS} />
                    </Col>
                  </Row>
                </Section>
                {companyId && (
                  <Section title="User Information">
                    <Row>
                      <Col xs={12}>
                        <TextInput
                          formik={formikProps}
                          name={USER_FIRSTNAME}
                          placeholder="Enter First Name"
                          isDisabled
                        />
                      </Col>
                      <Col xs={12}>
                        <TextInput
                          formik={formikProps}
                          name={USER_LASTNAME}
                          placeholder="Enter Last Name"
                          isDisabled
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12}>
                        <TextInput
                          formik={formikProps}
                          name={USER_EMAIL}
                          placeholder="Enter Email"
                          isDisabled
                        />
                      </Col>
                      <Col xs={12}>
                        <Uploader
                          draggable
                          action="http://localhost:3000/post"
                          fileList={
                            formikProps.values.imageUrl
                              ? [{ url: formikProps.values.imageUrl }]
                              : []
                          }
                          listType="picture"
                          fileInfo={fileInfo}
                          onChange={async (fileList) => {
                            if (fileList.length > 0) {
                              const file = fileList[fileList.length - 1]
                              if (!file.blobFile) {
                                notifyError('Invalid file selected', {
                                  containerId: 'modalToast'
                                })
                                return
                              }
                              const validImageTypes = [
                                'image/jpeg',
                                'image/jpg',
                                'image/png',
                                'image/gif',
                                'image/svg+xml',
                                'image/webp'
                              ]
                              if (
                                file.blobFile &&
                                !validImageTypes.includes(file.blobFile.type)
                              ) {
                                notifyError('Only image files are allowed ', {
                                  containerId: 'modalToast'
                                })
                                fileList.pop()
                                formikProps.setFieldValue(imageUrl, null)
                              } else if (
                                file.blobFile &&
                                file.blobFile.size > maxSize
                              ) {
                                notifyError(
                                  'File size must be less than 2 MB',
                                  {
                                    containerId: 'modalToast'
                                  }
                                )
                                fileList.pop()
                                formikProps.setFieldValue(imageUrl, null)
                              } else {
                                const base64 = await convertFileToBase64(
                                  file?.blobFile
                                )

                                previewFile(file.blobFile, (value: any) => {
                                  setFileInfo(value)
                                })
                                fileList.splice(0, fileList.length - 1)

                                formikProps.setFieldValue(imageUrl, base64)
                              }
                            } else {
                              formikProps.setFieldValue(imageUrl, null)
                            }
                          }}
                        />
                      </Col>
                    </Row>
                  </Section>
                )}
                {!companyId && (
                  <Section title="Company Documents">
                    <FieldArray name={DOCUMENTDTO}>
                      {(arrayHelpers) => {
                        const documents = formikProps.values[DOCUMENTDTO] ?? []

                        if (!documents.length) {
                          arrayHelpers.push({
                            documentType: '',
                            documentNo: '',
                            imageUrls: null,
                            fileInfo: ''
                          })
                        }

                        const selectedDocumentTypes = documents.map(
                          (doc) => doc.documentType
                        )
                        const documentOptions = Object.values(
                          DOCUMENT_TYPE
                        ).map((value) => ({
                          label: value,
                          // eslint-disable-next-line object-shorthand
                          value: value
                        }))
                        return (
                          // eslint-disable-next-line react/jsx-no-useless-fragment
                          <>
                            {documents?.map((doc, index) => {
                              const availableOptions = documentOptions.filter(
                                (option) =>
                                  !selectedDocumentTypes.includes(
                                    option.value
                                  ) || option.value === doc.documentType
                              )
                              return (
                                <Row key={index}>
                                  <Col xs={7}>
                                    <SelectPicker
                                      data={availableOptions ?? []}
                                      value={doc.documentType}
                                      onChange={(value) =>
                                        formikProps.setFieldValue(
                                          `${DOCUMENTDTO}.${index}.documentType`,
                                          value
                                        )
                                      }
                                      placeholder="Select Document Type"
                                      block
                                    />
                                    <FormikErrorMessage
                                      name={`${PROFILE_FORM_FIELDS.DOCUMENTDTO}[${index}].documentType`}
                                    />
                                  </Col>

                                  <Col xs={7}>
                                    <Input
                                      name={`${DOCUMENTDTO}.${index}.documentNo`}
                                      value={doc.documentNo ?? ''}
                                      onChange={(value) =>
                                        formikProps.setFieldValue(
                                          `${DOCUMENTDTO}.${index}.documentNo`,
                                          value
                                        )
                                      }
                                      placeholder="Enter Document Number"
                                    />

                                    <FormikErrorMessage
                                      name={`${PROFILE_FORM_FIELDS.DOCUMENTDTO}[${index}].documentNo`}
                                    />
                                  </Col>

                                  <Col xs={6}>
                                    <Uploader
                                      isVideo={false}
                                      draggable
                                      listType="picture"
                                      action="http://localhost:3000/post"
                                      fileInfo={
                                        typeof doc.fileInfo === 'string'
                                          ? doc.fileInfo
                                          : undefined
                                      }
                                      isPdf={
                                        typeof doc.fileInfo === 'string' &&
                                        doc.fileInfo.endsWith('.pdf')
                                      }
                                      onChange={async (fileList) => {
                                        if (fileList.length > 0) {
                                          const file =
                                            fileList[fileList.length - 1]
                                          if (file.blobFile) {
                                            const validImageTypes = [
                                              'image/jpeg',
                                              'image/jpg',
                                              'image/png',
                                              'image/gif',
                                              'image/svg+xml',
                                              'image/webp',
                                              'application/pdf'
                                            ]
                                            if (
                                              file.blobFile &&
                                              !validImageTypes.includes(
                                                file.blobFile.type
                                              )
                                            ) {
                                              notifyError(
                                                'Only Image And Pdf files are allowed ',
                                                {
                                                  containerId: 'modalToast'
                                                }
                                              )
                                              fileList.pop()
                                              formikProps.setFieldValue(
                                                `${DOCUMENTDTO}.${index}.imageUrls`,
                                                null
                                              )
                                              return
                                            }
                                            if (
                                              file.blobFile.size > MAX_FILE_SIZE
                                            ) {
                                              notifyError(
                                                'Size must be less than 2MB ',
                                                {
                                                  containerId: 'modalToast'
                                                }
                                              )
                                              return
                                            }
                                            try {
                                              const base64 =
                                                await convertFileToBase64(
                                                  file.blobFile
                                                )
                                              previewFile(
                                                file.blobFile,
                                                (value: any) => {
                                                  formikProps.setFieldValue(
                                                    `${DOCUMENTDTO}.${index}.fileInfo`,
                                                    value
                                                  )
                                                }
                                              )
                                              fileList.splice(
                                                0,
                                                fileList.length - 1
                                              )
                                              formikProps.setFieldValue(
                                                `${DOCUMENTDTO}.${index}.imageUrls`,
                                                base64
                                              )
                                            } catch (error) {
                                              // eslint-disable-next-line
                                              console.error(
                                                'Error converting file to base64:',
                                                error
                                              )
                                            }
                                          } else {
                                            formikProps.setFieldValue(
                                              `${DOCUMENTDTO}.${index}.imageUrls`,
                                              null
                                            )
                                          }
                                        }
                                      }}
                                    />
                                    <FormikErrorMessage
                                      name={`${PROFILE_FORM_FIELDS.DOCUMENTDTO}[${index}].imageUrls`}
                                    />
                                  </Col>

                                  {!(
                                    formikProps.values[DOCUMENTDTO].length ===
                                      1 || index === 0
                                  ) && (
                                    <Col xs={2}>
                                      <Button
                                        className="circular-btn"
                                        appearance="subtle"
                                        onClick={() =>
                                          arrayHelpers.remove(index)
                                        }
                                      >
                                        <MdRemoveCircle color="red" />
                                      </Button>
                                    </Col>
                                  )}
                                  {index ===
                                    formikProps.values[DOCUMENTDTO].length -
                                      1 &&
                                    formikProps.values[DOCUMENTDTO][index]
                                      .documentType && (
                                      <Col xs={2}>
                                        <Button
                                          className="circular-btn"
                                          onClick={() =>
                                            arrayHelpers.push({
                                              documentType: '',
                                              documentNo: '',
                                              imageUrls: null,
                                              fileInfo: ''
                                            })
                                          }
                                          disabled={
                                            formikProps.values[DOCUMENTDTO]
                                              .length >= 3
                                          }
                                        >
                                          <Whisper
                                            placement="bottom"
                                            trigger="hover"
                                            speaker={
                                              <Tooltip>
                                                {formikProps.values[DOCUMENTDTO]
                                                  .length >= 3
                                                  ? 'Maximum 3 documents allowed'
                                                  : 'Add Document'}
                                              </Tooltip>
                                            }
                                          >
                                            <PlusIcon
                                              color={
                                                formikProps.values[DOCUMENTDTO]
                                                  .length >= 3
                                                  ? 'gray'
                                                  : 'green'
                                              }
                                            />
                                          </Whisper>
                                        </Button>
                                      </Col>
                                    )}
                                </Row>
                              )
                            })}
                          </>
                        )
                      }}
                    </FieldArray>
                  </Section>
                )}
              </Panel>
              <Panel className="panel-buttons">
                {renderFormButtons(formikProps)}
              </Panel>
            </Form>
          )}
        </Formik>
      }
    />
  )
}
export default CreateEditProfile
